import React, { useState, useEffect } from 'react';
import { Grid, Autocomplete, TextField, Box, Chip } from '@mui/material';

function LocationSelector({ onChange, initialValues }) {
    const [prefectures, setPrefectures] = useState([]);
    const [cities, setCities] = useState([]);
    const [lines, setLines] = useState([]);
    const [stations, setStations] = useState([]);
    const [selectedPrefecture, setSelectedPrefecture] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedLine, setSelectedLine] = useState(null);
    const [selectedStation, setSelectedStation] = useState(null);

    // Autocompleteの比較関数を追加
    const isOptionEqualToValue = (option, value) => {
        if (!option || !value) return false;
        // 都道府県、市区町村、路線、駅のいずれかの比較
        return option.name === value.name ||
            option.code === value.code ||
            option.lineCode === value.lineCode;
    };

    // 都道府県一覧を取得（一度だけ実行）
    useEffect(() => {
        const fetchPrefectures = async () => {
            try {
                const response = await fetch('/api/locations/prefectures');
                const data = await response.json();
                setPrefectures(Array.isArray(data) ? data : []);

                // 初期値の都道府県を設定
                if (initialValues?.prefecture && data.length > 0) {
                    const prefecture = data.find(p => p.name === initialValues.prefecture);
                    if (prefecture) {
                        setSelectedPrefecture(prefecture);
                    }
                }
            } catch (err) {
                console.error('都道府県データの取得に失敗:', err);
                setPrefectures([]);
            }
        };
        fetchPrefectures();
    }, [initialValues?.prefecture]);

    // 市区町村一覧を取得（都道府県が選択されたときのみ）
    useEffect(() => {
        let isMounted = true;

        const fetchCities = async () => {
            if (!selectedPrefecture) return;

            try {
                const response = await fetch(`/api/locations/cities?prefectureCode=${selectedPrefecture.code}`);
                const data = await response.json();
                if (isMounted) {
                    setCities(Array.isArray(data) ? data : []);

                    // 初期値の市区町村を設定
                    if (initialValues?.city) {
                        const city = data.find(c => c.name === initialValues.city);
                        if (city) {
                            setSelectedCity(city);
                        }
                    }
                }
            } catch (err) {
                console.error('市区町村データの取得に失敗:', err);
                if (isMounted) setCities([]);
            }
        };

        fetchCities();
        return () => { isMounted = false; };
    }, [selectedPrefecture, initialValues?.city]);

    // 路線一覧を取得と初期値設定
    useEffect(() => {
        let isMounted = true;

        const fetchLines = async () => {
            if (!selectedPrefecture) return;

            try {
                const response = await fetch(`/api/locations/lines?prefectureCode=${selectedPrefecture.code}`);
                const data = await response.json();
                if (isMounted) {
                    const linesList = Array.isArray(data) ? data : [];
                    setLines(linesList);

                    // 初期値の路線を設定
                    if (initialValues?.line) {
                        const line = linesList.find(l => l.name === initialValues.line);
                        if (line) {
                            setSelectedLine(line);
                        }
                    }
                }
            } catch (err) {
                console.error('路線データの取得に失敗:', err);
                if (isMounted) setLines([]);
            }
        };

        fetchLines();
        return () => { isMounted = false; };
    }, [selectedPrefecture, initialValues?.line]);

    // 駅一覧を取得と初期値設定
    useEffect(() => {
        let isMounted = true;

        const fetchStations = async () => {
            if (!selectedLine || !selectedPrefecture) return;

            try {
                const response = await fetch(
                    `/api/locations/stations?lineCode=${selectedLine.lineCode}&prefectureCode=${selectedPrefecture.code}`
                );
                const data = await response.json();
                if (isMounted) {
                    const stationsList = Array.isArray(data) ? data : [];
                    setStations(stationsList);

                    // 初期値の駅を設定
                    if (initialValues?.station) {
                        const station = stationsList.find(s => s.name === initialValues.station);
                        if (station) {
                            setSelectedStation(station);
                        }
                    }
                }
            } catch (err) {
                console.error('駅データの取得に失敗:', err);
                if (isMounted) setStations([]);
            }
        };

        fetchStations();
        return () => { isMounted = false; };
    }, [selectedLine, selectedPrefecture, initialValues?.station]);

    // 選択が変されたときの処理
    const handlePrefectureChange = (event, value) => {
        setSelectedPrefecture(value);
        setSelectedCity(null);
        setSelectedLine(null);
        setSelectedStation(null);
        onChange({ prefecture: value, city: null, line: null, station: null });
    };

    const handleCityChange = (event, value) => {
        setSelectedCity(value);
        setSelectedLine(null);
        setSelectedStation(null);
        onChange({ prefecture: selectedPrefecture, city: value, line: null, station: null });
    };

    const handleLineChange = (event, value) => {
        setSelectedLine(value);
        setSelectedStation(null);
        onChange({
            prefecture: selectedPrefecture,
            city: selectedCity,
            line: value,
            station: null
        });
    };

    const handleStationChange = (event, value) => {
        setSelectedStation(value);
        onChange({ prefecture: selectedPrefecture, city: selectedCity, line: selectedLine, station: value });
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
                <Autocomplete
                    value={selectedPrefecture}
                    onChange={handlePrefectureChange}
                    options={Array.isArray(prefectures) ? prefectures : []}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={isOptionEqualToValue}
                    renderInput={(params) => (
                        <TextField {...params} label="都道府県" variant="outlined" />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <Autocomplete
                    value={selectedCity}
                    onChange={handleCityChange}
                    options={Array.isArray(cities) ? cities : []}
                    getOptionLabel={(option) => option?.name || ''}
                    disabled={!selectedPrefecture}
                    renderInput={(params) => (
                        <TextField {...params} label="市区町村" variant="outlined" />
                    )}
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <Box component="li" key={option.code} {...otherProps}>
                                {option.name}
                            </Box>
                        );
                    }}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <Autocomplete
                    value={selectedLine}
                    onChange={handleLineChange}
                    options={Array.isArray(lines) ? lines : []}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={isOptionEqualToValue}
                    disabled={!selectedPrefecture}
                    renderInput={(params) => (
                        <TextField {...params} label="路線" variant="outlined" />
                    )}
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <Box component="li" key={option.lineCode} {...otherProps}>
                                <Chip
                                    label={option.name}
                                    size="small"
                                    sx={{
                                        backgroundColor: option.color || '#ccc',
                                        color: '#fff'
                                    }}
                                />
                            </Box>
                        );
                    }}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <Autocomplete
                    value={selectedStation}
                    onChange={handleStationChange}
                    options={Array.isArray(stations) ? stations : []}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={isOptionEqualToValue}
                    disabled={!selectedLine}
                    renderInput={(params) => (
                        <TextField {...params} label="駅" variant="outlined" />
                    )}
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <Box component="li" key={option.name} {...otherProps}>
                                {option.name}
                            </Box>
                        );
                    }}
                />
            </Grid>
        </Grid>
    );
}

export default LocationSelector; 